import { useDappState } from '@src/hooks/useDappState';
import { ProductSKU } from 'jigsaw';
import { BiLockOpen } from 'react-icons/bi';
import { Button, Text, VStack } from 'ui_2';
import { renderPartTitle } from '@src/data/utils';

const ProductImage = ({
  imageURL,
  url,
  title,
}: {
  imageURL: string;
  url?: string;
  title: string;
}) => {
  const imageElement = (
    <img
      src={imageURL}
      alt={title}
      className='h-[42px] max-h-full w-auto max-w-full overflow-hidden rounded-lg border border-gray-100 object-contain shadow-lg'
    />
  );

  return url ? (
    <a href={url} target='_blank' className='w-fit'>
      {imageElement}
    </a>
  ) : (
    imageElement
  );
};

export const ProductSkuCard = ({ sku }: { sku: ProductSKU }) => {
  const { isPuzzleWalletWebview, partBalance, isPro } = useDappState();
  const price = sku.price;
  const imageURL = sku.bigImageURL ?? sku.service.bigLogoImageURL;
  const url = sku.url ?? sku.service.url;
  const title = sku.title;

  const partRemaining = price - (partBalance ?? 0);
  const percentage = Math.min(Math.max((partBalance ?? 0) / price, 0), 1);

  // Progress arc configuration
  const arcWidth = 12; // Adjust this value to make the arc wider or narrower
  // Calculate the actual arc length (semi-circle with radius 80)
  const radius = 130;
  const arcLength = Math.PI * radius; // Full semi-circle length
  const progressLength = percentage * arcLength;

  return (
    <div className='relative max-w-[130px] rounded-lg bg-white'>
      {/* Semi-circular progress bar */}
      <div className='relative h-[120px] w-full origin-top scale-[1.25]'>
        <svg className='h-full w-full' viewBox='-50 0 300 100'>
          {/* Background arc */}
          <path
            d='M -30 80 A 80 80 0 0 1 230 80'
            fill='none'
            stroke='#e5e7eb'
            strokeWidth={arcWidth}
            // strokeLinecap='round'
          />
          {/* Progress arc */}
          <path
            d='M -30 80 A 80 80 0 0 1 230 80'
            fill='none'
            stroke='#FFA3A3'
            strokeWidth={arcWidth}
            // strokeLinecap='round'
            strokeDasharray={`${progressLength} ${arcLength}`}
            strokeDashoffset='0'
          />
        </svg>
      </div>

      <VStack className='relative z-10 -mt-[72px] items-center justify-center gap-1'>
        {/* Gift card image */}
        {imageURL && (
          <ProductImage imageURL={imageURL} url={url} title={title} />
        )}
        <VStack className='w-[200px] text-center'>
          {/* Product title */}
          <div className='text-center'>
            <Text font='medium-sm'>{title.replace(/gift/gi, '')}</Text>
          </div>

          {/* Remaining parts needed */}
          <div className='text-center'>
            {partBalance && partRemaining <= 0 ? (
              isPro ? (
                <Button
                  onClick={() => {
                    if (isPuzzleWalletWebview) {
                      window.open('https://app.puzzle.online/store', '_blank');
                    } else {
                      window.open('https://store.puzzle.online', '_blank');
                    }
                  }}
                  size='sm'
                >
                  <Text font='xs' color='white'>
                    Purchase
                  </Text>
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    window.open('https://app.puzzle.online/paywall', '_blank')
                  }
                  size='sm'
                  variant='black'
                  className='items-center gap-1'
                >
                  <BiLockOpen className='text-white' />
                  <Text font='xs' color='white'>
                    Unlock with Pro
                  </Text>
                </Button>
              )
            ) : (
              <Text font='sm' className='text-green-800'>
                {renderPartTitle(`${partRemaining} more PART!`)}
              </Text>
            )}
          </div>
        </VStack>
      </VStack>
    </div>
  );
};
